import { onUnmounted, watch, type Ref } from 'vue'

interface UseGlobalContextmenuOptions {
  /** 是否启用全局右键监听 */
  enabled: Ref<boolean>
  /** 触发右键菜单时的回调 */
  onContextmenu: (event: MouseEvent) => void
  /** 是否阻止浏览器默认右键菜单，默认 true */
  preventDefault?: boolean
  /** 是否阻止事件冒泡，默认 false */
  stopPropagation?: boolean
  /** addEventListener capture 参数，默认 true */
  capture?: boolean
}

/**
 * 全局 contextmenu 监听器。
 *
 * 使用方式：
 * - enabled 为 true 时自动开始监听
 * - enabled 为 false 时自动移除监听
 * - 组件卸载时自动清理
 */
export function useGlobalContextmenu(options: UseGlobalContextmenuOptions) {
  const {
    enabled,
    onContextmenu,
    preventDefault = true,
    stopPropagation = false,
    capture = true,
  } = options

  let listening = false

  /** 全局右键事件处理 */
  const handler = (event: MouseEvent) => {
    if (preventDefault) {
      event.preventDefault()
    }
    if (stopPropagation) {
      event.stopPropagation()
    }

    onContextmenu(event)
  }

  /** 开始监听 */
  const start = () => {
    if (listening || typeof window === 'undefined') return
    window.addEventListener('contextmenu', handler, capture)
    listening = true
  }

  /** 停止监听 */
  const stop = () => {
    if (!listening || typeof window === 'undefined') return
    window.removeEventListener('contextmenu', handler, capture)
    listening = false
  }

  watch(
    enabled,
    (value) => {
      if (value) start()
      else stop()
    },
    { immediate: true },
  )

  onUnmounted(() => {
    stop()
  })

  return {
    start,
    stop,
    isListening: () => listening,
  }
}
